import { useEffect, Suspense } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { Loading } from '@fagmobler/ui';
import type { Breadcrumbs as TBreadcrumbs } from '@fagmobler/types';

import { useNavigation } from '@/contexts/navigation';

import {
  ClubMemberDrawer,
  PurchaseTermsDrawer,
  MemberTermsDrawer,
  StoreLocatorDrawer,
} from '@/components/drawers';
import StoreStockModal from '@/components/store/StoreStockModal';

import Header from './Header';
import Footer from './Footer';
import { BreadcrumbJsonLd, SiteLinksSearchBoxJsonLd } from 'next-seo';
import { Breadcrumbs } from '../breadcrumbs';

const HeaderSearchWrapper = dynamic(() => import('./Header/search'), {
  suspense: true,
});

const Cart = dynamic(() => import('@/components/cart/Cart'), {
  ssr: false,
});

export type DefaultLayoutProps = {
  cataloguePath?: string;
  breadcrumbs?: TBreadcrumbs;
  loading?: boolean;
  isPreview?: boolean;
  children: React.ReactNode;
  fullwidth?: boolean;
};

export const DefaultLayout = ({
  children,
  breadcrumbs,
  loading,
  isPreview,
  fullwidth = false,
}: DefaultLayoutProps) => {
  const router = useRouter();
  const routerEvents = router?.events;
  const { showSearch = false, setShowMainNavigation } = useNavigation();

  const searchVisible = router.query['q'] || showSearch;

  useEffect(() => {
    document.body.tabIndex = 0;

    const handleRouteChange = (url: string, { shallow }: any) => {
      // Focus body when router changes, ensures we close the main navigation
      // menu, as it will only close if focus is moved outside the menu.
      if (!shallow) {
        document.body.focus();
        setShowMainNavigation(false);
      }
    };
    if (routerEvents) {
      routerEvents.on('routeChangeComplete', handleRouteChange);

      return () => {
        routerEvents.off('routeChangeComplete', handleRouteChange);
      };
    }
  }, [routerEvents, setShowMainNavigation]);

  useEffect(() => {
    if (showSearch) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [showSearch]);

  return (
    <>
      <SiteLinksSearchBoxJsonLd
        url="https://fagmobler.no"
        potentialActions={[
          {
            queryInput: 'search_term_string',
            target: 'https://fagmobler.no/?q',
          },
        ]}
      />
      {isPreview && (
        <div className="bg-primary-50 text-neutral-0 p-2 flex justify-between text-sm">
          <p>
            Dette er en prøveversjon av siden. Trykk <b>publish</b> i Sanity for
            å publisere denne siden nå.
          </p>

          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a
            href="/api/preview?enable=false"
            className="bg-white text-neutral-80 rounded px-5 whitespace-nowrap flex align-center justify-center"
          >
            Skru av
          </a>
        </div>
      )}
      <Header />

      {router && !router.pathname.includes('/utsjekk') && <Cart />}

      <div className="print:hidden">
        {breadcrumbs && <Breadcrumbs breadcrumbs={breadcrumbs} />}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <BreadcrumbJsonLd
            itemListElements={breadcrumbs?.map((crumb, i) => {
              return {
                position: i + 1,
                name: crumb.name,
                item: `https://fagmobler.no/${crumb.path}`,
              };
            })}
          />
        )}
      </div>
      <div
        className={clsx(
          { 'xl:container': !fullwidth },
          'globalPadding relative print:max-w-full print:mx-0 print:px-0'
        )}
      >
        {children}
      </div>
      <div className="print:hidden">
        {searchVisible && (
          <Suspense fallback={<Loading fullscreen />}>
            <HeaderSearchWrapper />
          </Suspense>
        )}
        <StoreLocatorDrawer />
        <ClubMemberDrawer />
        <PurchaseTermsDrawer />
        <MemberTermsDrawer />
        <StoreStockModal />
        {loading && <Loading fullscreen showBackground />}
      </div>
      <Footer />
    </>
  );
};

export default DefaultLayout;
